
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import DashboardSidebar from "@/components/DashboardSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import VehicleHeader from "@/components/vehicle/VehicleHeader";
import VehicleImage from "@/components/vehicle/VehicleImage";
import VehicleDetailsTable from "@/components/vehicle/VehicleDetailsTable";
import VehicleParkingSection from "@/components/vehicle/VehicleParkingSection";
import { VehicleWishlist } from "@/components/vehicle/VehicleWishlist";
import { MaintenancePlanner } from "@/components/vehicle/MaintenancePlanner";
import { ChiptuningOptions } from "@/components/vehicle/ChiptuningOptions";

const VehicleDetailsPage = () => {
  const { id } = useParams();

  const { data: vehicle, isLoading, error, refetch } = useQuery({
    queryKey: ["vehicle", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("vehicles")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      return data;
    },
  });

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error || !vehicle) {
    return <div>Error loading vehicle details</div>;
  }

  const vehicleDetails = [
    { label: "Merk", value: vehicle.brand },
    { label: "Model", value: vehicle.model },
    { label: "Bouwjaar", value: vehicle.year?.toString() },
    { label: "Kleur", value: vehicle.color },
    { label: "Brandstof", value: vehicle.fuel_type },
    { label: "Kenteken", value: vehicle.license_plate },
    { label: "Transmissie", value: vehicle.transmission_type },
    { label: "Vermogen (PK)", value: vehicle.power_hp?.toString() },
    { label: "Vermogen (KW)", value: vehicle.power_kw?.toString() },
    { label: "Registratie Datum", value: vehicle.registration_date },
    { label: "APK Vervaldatum", value: vehicle.apk_date },
  ];

  const overviewDetails = [
    { label: "Transmissie", value: vehicle.transmission_type },
    { label: "Kleur", value: vehicle.color },
    { label: "Brandstof", value: vehicle.fuel_type },
  ];

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <DashboardSidebar />
        <main className="flex-1 overflow-auto bg-gray-50">
          <VehicleHeader
            year={vehicle.year?.toString() || ''}
            brand={vehicle.brand || ''}
            model={vehicle.model || ''}
            licensePlate={vehicle.license_plate}
          />

          <div className="px-8 py-8">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
                <div className="space-y-8 h-full flex flex-col">
                  <VehicleImage
                    imageUrl={vehicle.image_url}
                    vehicleId={id!}
                    onImageUpdate={refetch}
                  />

                  <div className="space-y-6 bg-white p-6 rounded-lg shadow-sm flex-grow">
                    <h3 className="text-lg font-semibold text-gray-900">Overzicht</h3>
                    <div className="space-y-6">
                      {overviewDetails.map((detail) => (
                        <div key={detail.label} className="border-b border-gray-200 pb-6 last:border-b-0 last:pb-0">
                          <div className="flex flex-col space-y-1">
                            <span className="text-sm text-gray-600 font-medium">{detail.label}</span>
                            <span className="text-gray-900">{detail.value}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm h-full">
                  <h2 className="text-xl font-semibold mb-6 text-gray-900">Voertuig Details</h2>
                  <VehicleDetailsTable details={vehicleDetails} />
                </div>

                <div className="space-y-8 h-full flex flex-col">
                  <VehicleWishlist vehicleId={id!} />
                  <VehicleParkingSection
                    vehicleId={id!}
                    initialParkingDetails={{
                      address: vehicle.parking_address || "",
                      lat: vehicle.parking_lat,
                      lng: vehicle.parking_lng
                    }}
                    onParkingUpdate={refetch}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <MaintenancePlanner vehicleId={id!} />
                <ChiptuningOptions 
                  vehicleId={id!} 
                  currentPowerHp={vehicle.power_hp}
                  brand={vehicle.brand}
                  model={vehicle.model}
                />
              </div>
            </div>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default VehicleDetailsPage;
